import useLocale from '@/i18n/useLocale'
import { useCallback, useMemo, useRef } from 'react'
import { useInterval } from '@betterplace/design-system/client'
import { useTranslations } from 'next-intl'

const UNITS_IN_MILLISECS = {
  year: 31536000000,
  month: 2628000000,
  day: 86400000,
  hour: 3600000,
  minute: 60000,
  second: 1000,
} as const

const LOCALIZED_RFT = {
  de: new Intl.RelativeTimeFormat('de', { numeric: 'auto' }),
  en: new Intl.RelativeTimeFormat('en', { numeric: 'auto' }),
} as const

function useRelativeTimeFromTimestamp(timestamp: string) {
  const locale = useLocale()
  const t = useTranslations('nextjs.core')
  const timestampTime = useMemo(() => new Date(timestamp).getTime(), [timestamp])
  const nowishTimeRef = useRef<number>(Date.now())

  const updateNowishTime = useCallback(() => {
    nowishTimeRef.current = Date.now()
  }, [])

  useInterval({ callback: updateNowishTime, intervalMs: 10000 })
  const elapsedTime = nowishTimeRef.current - timestampTime

  if (elapsedTime < UNITS_IN_MILLISECS.minute) {
    return t('just_now')
  }

  const unit = Object.keys(UNITS_IN_MILLISECS).find(
    (key) => elapsedTime >= UNITS_IN_MILLISECS[key as keyof typeof UNITS_IN_MILLISECS]
  ) as keyof typeof UNITS_IN_MILLISECS

  return LOCALIZED_RFT[locale].format(-Math.round(elapsedTime / UNITS_IN_MILLISECS[unit]), unit)
}

export default useRelativeTimeFromTimestamp
